import FVector from '@data/geometry/FVector';
import { BinaryReader } from 'Loader/serialization/common/BinaryReader';
import HeaderSerializer from 'Loader/serialization/serializers/v0_0_13/binaryData/HeaderSerializer';
import Header from '../common/Header';
import { findSerializer } from '../common/SerializerFinder';
import { SerializerKind } from '../common/Constants';
import { Jumper } from './Jumper.js';

export abstract class RaysJumper extends Jumper {
    protected binaryReader: BinaryReader;
    protected headerSerializer: HeaderSerializer;
    override kind: 'rays' = 'rays';

    constructor(reader: BinaryReader, headerSerializer: HeaderSerializer) {
        super();
        this.binaryReader = reader;
        this.headerSerializer = headerSerializer;
    }

    abstract getRays(
        sp: number,
        frame: number
    ): {
        dir: FVector;
        value: number;
        params?: { param1: number; param2: number };
    }[];

    override dispose(): void {
        this.binaryReader = undefined as unknown as BinaryReader;
        this.headerSerializer = undefined as unknown as HeaderSerializer;
    }
}

export async function getRaysJumper(binaryReader: BinaryReader) {
    const vMajor = binaryReader.readInt32();
    const vMinor = binaryReader.readInt32();
    const vRelease = binaryReader.readInt32();
    const readVersion = `v${vMajor}_${vMinor}_${vRelease}`;

    if (vMajor != 0 || vMinor != 0 || vRelease < 2) {
        throw new Error("Serializer don't exists");
    }

    const serializerHeaderInstance: any = findSerializer(readVersion, Header, SerializerKind.BINARY_DATA).serializer;

    let jumper;
    if (vRelease < 14) jumper = new RaysJumperV0_0_2(binaryReader, serializerHeaderInstance);
    else jumper = new RaysJumperV0_0_14(binaryReader, serializerHeaderInstance);

    binaryReader.reset();
    await jumper.init();
    return jumper;
}

export class RaysJumperV0_0_2 extends RaysJumper {
    private rayPoolPosition: number = 0;
    private params: boolean = false;
    private paramsPosition: number = 0;
    private frameSpPosition: number[][] = [];
    private isEqual: boolean[] = [];
    private r1PerFrame: number[] = [];
    private indexPerFramePositon: number[] = [];
    private min: number = Number.MAX_VALUE;
    private max: number = Number.MIN_VALUE;

    constructor(reader: BinaryReader, headerSerializer: HeaderSerializer) {
        super(reader, headerSerializer);
    }

    override dispose(): void {
        this.frameSpPosition = [];
        this.isEqual = [];
        this.r1PerFrame = [];
        this.indexPerFramePositon = [];
        this.rayPoolPosition = 0;
        this.params = false;
        this.paramsPosition = 0;
        super.dispose();
    }

    protected async readHeader() {
        const headerData = await this.headerSerializer.read(this.binaryReader);
        return {
            nSP: headerData.numSensPnts,
        };
    }

    protected readDimensions() {
        const nFrames = this.binaryReader.readUInt8();
        const nRaysRayPool = this.binaryReader.readUInt32();
        return {
            nFrames,
            nRaysRayPool,
        };
    }

    protected readRayPool(nRaysRayPool: number) {
        this.rayPoolPosition = this.binaryReader.getPos();

        this.binaryReader.skip(nRaysRayPool * 4 * 3);
        this.params = this.binaryReader.readBoolean();

        if (this.params) {
            this.paramsPosition = this.binaryReader.getPos();
            this.binaryReader.skip(4 * 2 * nRaysRayPool);
        }
    }

    protected readFrames(nFrames: number, nSP: number) {
        this.frameSpPosition = new Array(nFrames);
        this.isEqual = new Array(nFrames);
        this.r1PerFrame = new Array(nFrames);
        this.indexPerFramePositon = new Array(nFrames);
        for (let f = 0; f < nFrames; f++) {
            this.frameSpPosition[f] = new Array(nSP);

            console.debug(this.binaryReader.readString());

            this.isEqual[f] = this.binaryReader.readBoolean();
            let r1 = 0;
            for (let n = 0; n < nSP; n++) {
                this.frameSpPosition[f][n] = this.binaryReader.getPos();
                if (!this.isEqual[f] || n === 0) {
                    r1 = this.binaryReader.readUInt32();

                    // if every sp has same number of rays we save it
                    if (this.isEqual[f]) this.r1PerFrame[f] = r1;
                }

                for (let r = 0; r < r1; r++) {
                    const value = Math.abs(this.binaryReader.readFloat32());
                    if (value < this.min) this.min = value;
                    if (value > this.max) this.max = value;
                }

                if (this.isEqual[f] && n > 0) {
                    continue;
                }

                if (this.isEqual[f] && n === 0) this.indexPerFramePositon[f] = this.binaryReader.getPos();
                const isContinue = this.binaryReader.readBoolean();

                if (isContinue) this.binaryReader.skip(4);
                else this.binaryReader.skip(4 * r1);
            }
        }
    }

    override async init() {
        const { nSP } = await this.readHeader();
        const { nFrames, nRaysRayPool } = this.readDimensions();
        this.readRayPool(nRaysRayPool);
        this.readFrames(nFrames, nSP);
    }

    override getRays(
        sp: number,
        frame: number
    ): {
        dir: FVector;
        value: number;
        params?: { param1: number; param2: number };
    }[] {
        let rays: {
            dir: FVector;
            value: number;
            params?: { param1: number; param2: number };
        }[] = [];
        const tmpFVec = new FVector();

        this.binaryReader.setPos(this.frameSpPosition[frame][sp]);

        let r1 = 0;
        if (!this.isEqual[frame] || sp == 0) r1 = this.binaryReader.readUInt32();
        else r1 = this.r1PerFrame[frame];

        for (let r = 0; r < r1; r++) {
            rays.push({
                dir: tmpFVec,
                value: this.binaryReader.readFloat32(),
            });
        }

        if (this.isEqual[frame] && sp > 0) this.binaryReader.setPos(this.indexPerFramePositon[frame]);

        const isContinue = this.binaryReader.readBoolean();
        if (isContinue) {
            const index = this.binaryReader.readUInt32();
            for (let i = 0; i < r1; i++) {
                this.binaryReader.setPos(this.rayPoolPosition + (index + i) * 4 * 3);
                rays[i].dir = this.binaryReader.readFVector();

                rays[i].params = this.readParams(index + i);
            }
        } else {
            const indexPosition = this.binaryReader.getPos();
            for (let i = 0; i < r1; i++) {
                this.binaryReader.setPos(indexPosition + 4 * i);
                const index = this.binaryReader.readUInt32();
                this.binaryReader.setPos(this.rayPoolPosition + 4 * 3 * index);
                rays[i].dir = this.binaryReader.readFVector();

                rays[i].params = this.readParams(index);
            }
        }

        return rays;
    }

    override getMin(): number {
        return this.min;
    }

    override getMax(): number {
        return this.max;
    }

    private readParams(index: number) {
        if (!this.params) return undefined;
        this.binaryReader.setPos(this.paramsPosition + index * 4 * 2);
        return {
            param1: this.binaryReader.readFloat32(),
            param2: this.binaryReader.readFloat32(),
        };
    }
}

export class RaysJumperV0_0_14 extends RaysJumperV0_0_2 {
    protected override readDimensions() {
        this.binaryReader.readUInt32();
        const nFrames = this.binaryReader.readUInt8();
        const nRaysRayPool = this.binaryReader.readUInt32();
        return {
            nFrames,
            nRaysRayPool,
        };
    }
}
